<template>
  <v-card flat>
    <v-card-text class="pa-2">
      <div class="py-1 custom-left-border" :class="getBorderClass(item.priceIndex)">
        <!-- Price Row -->
        <div class="mb-1">
          <div class="d-flex align-center">
            <!-- Seller -->
            <v-tooltip top v-if="item.seller">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" x-small class="mr-1 grey--text text--darken-2">
                  mdi-storefront
                </v-icon>
              </template>
              <span>{{ item.seller }}</span>
            </v-tooltip>

            <template v-if="item.price > 0">
              <a
                v-if="item.url"
                :href="item.url"
                target="_blank"
                class="font-weight-bold text-body-2 mr-1 text-decoration-none grey--text text--darken-1"
              >
                {{ formatPrice(item.price) }}
              </a>
              <span v-else class="text-body-2 font-weight-bold mr-1 grey--text text--darken-1">
                {{ formatPrice(item.price) }}
              </span>
            </template>
            <v-tooltip top v-else>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-flex align-center cursor-help mr-1">
                  <span class="text-body-2 grey--text font-italic mr-1">Pending</span>
                  <v-icon x-small color="grey">mdi-help-circle-outline</v-icon>
                </div>
              </template>
              <span>Price pending. Report if issue persists.</span>
            </v-tooltip>
            <v-tooltip top v-if="isStale">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="text-body-2 font-weight-bold grey--text text--darken-1 mr-1 cursor-help"
                  >-</span
                >
              </template>
              <span>Price not updated recently</span>
            </v-tooltip>
          </div>
          <!-- Price Index -->
          <div
            v-if="item.priceIndex !== undefined && item.priceIndex !== null && item.price > 0"
            class="d-flex align-center"
            style="line-height: 1"
          >
            <span class="text-caption" :class="getPriceIndexColor(item.priceIndex)">
              {{ item.priceIndex > 0 ? '+' : '' }}{{ item.priceIndex.toFixed(2) }}%
            </span>
          </div>
        </div>

        <!-- Indicators Row -->
        <div class="d-flex align-center flex-nowrap">
          <!-- Price Trend Indicator -->
          <v-tooltip top v-if="computedStatus && item.price > 0">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                x-small
                class="mr-1"
                :color="getStatusColor(computedStatus)"
              >
                {{ getStatusIcon(computedStatus) }}
              </v-icon>
            </template>
            <span>Price went {{ computedStatus }}</span>
          </v-tooltip>

          <!-- Availability (S) -->
          <v-tooltip top v-if="item.availability && item.price > 0">
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="mr-1 font-weight-bold text-caption"
                style="cursor: pointer"
                :class="getAvailabilityColor(item.availability) + '--text'"
              >
                S
              </span>
            </template>
            <span>{{ formatAvailability(item.availability) }}</span>
          </v-tooltip>

          <!-- Promotion (P) -->
          <v-tooltip top v-if="item.promotion">
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="mr-1 font-weight-bold cursor-help text-caption orange--text text--darken-2"
              >
                P
              </span>
            </template>
            <span>Promotion Active</span>
          </v-tooltip>

          <!-- Google Shopping (G) -->
          <v-tooltip top v-if="item.isGoogleShoppingProduct">
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="mr-1 font-weight-bold text-caption primary--text"
                style="cursor: pointer"
              >
                G
              </span>
            </template>
            <span>Google Shopping Product</span>
          </v-tooltip>

          <!-- Position -->
          <!-- <v-tooltip top v-if="item.position && item.position !== 'Unknown'">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on" class="d-flex align-center grey--text text--darken-1">
            <v-icon x-small class="mr-1">mdi-podium</v-icon>
            <span class="text-caption">{{ item.position }}</span>
          </div>
        </template>
        <span>Position: {{ item.position }}</span>
      </v-tooltip> -->
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
  import moment from 'moment'

  export default {
    name: 'TablePriceDisplay',
    props: {
      item: {
        type: Object,
        required: true,
        default: () => ({}),
      },
    },
    computed: {
      isStale() {
        if (!this.item.updateDate) return false
        const offerUpdateDate = moment(this.item.updateDate).format('YYYY-MM-DD')
        const dateToCompareWith =
          moment().diff(moment(this.item.updateDate), 'days') <= 1
            ? moment(this.item.updateDate).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD')
        return offerUpdateDate != dateToCompareWith
      },
      computedStatus() {
        if (!this.item.updateDate) return null
        const offerUpdateDate = moment(this.item.updateDate).format('YYYY-MM-DD')
        const offerChangeDate = moment(this.item.changeDate).format('YYYY-MM-DD')
        const dateToCompareWith =
          moment().diff(moment(this.item.updateDate), 'days') <= 1
            ? moment(this.item.updateDate).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD')

        if (offerUpdateDate == dateToCompareWith) {
          if (offerChangeDate == dateToCompareWith) {
            return this.item.status
          } else {
            return 'stable'
          }
        }
        return null
      },
    },
    methods: {
      formatPrice(price) {
        if (price === undefined || price === null) return 'N/A'

        let country = 'es'
        try {
          if (localStorage.user_website) {
            country = JSON.parse(localStorage.user_website).country
          }
        } catch (e) {
          // Fallback to default
        }

        let locale = 'es-ES'
        let currency = 'EUR'

        switch (country) {
          case 'mx':
            locale = 'es-MX'
            currency = 'MXN'
            break
          case 'es':
            locale = 'es-ES'
            currency = 'EUR'
            break
          case 'ch':
            locale = 'de-CH'
            currency = 'CHF'
            break
          case 'uk':
            locale = 'en-GB'
            currency = 'GBP'
            break
          case 'do':
            locale = 'es-DO'
            currency = 'DOP'
            break
          default:
            locale = 'es-ES'
            currency = 'EUR'
        }

        return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(
          price,
        )
      },
      formatAvailability(availability) {
        if (!availability) return 'Unknown'
        // Add space before capital letters if needed, or just return as is
        return availability.replace(/([A-Z])/g, ' $1').trim()
      },
      getAvailabilityColor(availability) {
        const status = availability?.toLowerCase()
        if (status === 'instock') return 'success'
        if (status === 'outofstock') return 'error'
        return 'grey'
      },
      getStatusColor(status) {
        if (status === 'up') return 'red'
        if (status === 'down') return 'green'
        return 'grey'
      },
      getStatusIcon(status) {
        if (status === 'up') return 'mdi-arrow-up'
        if (status === 'down') return 'mdi-arrow-down'
        return 'mdi-equal'
      },
      getPriceIndexColor(index) {
        if (index > 0) return 'red--text text--lighten-1'
        if (index === 0) return 'amber--text text--darken-2'
        return 'green--text text--lighten-1'
      },
      getBorderClass(index) {
        if (index === undefined || index === null) return ''
        if (index > 0) return 'border-red'
        if (index === 0) return 'border-yellow'
        return 'border-green'
      },
    },
  }
</script>

<style scoped>
  .hover-underline:hover {
    text-decoration: underline !important;
    color: #1976d2 !important;
  }
  .cursor-help {
    cursor: help;
  }
  .custom-left-border {
    position: relative;
    padding-left: 10px;
  }
  .custom-left-border::before {
    content: '';
    position: absolute;
    left: 0;
    top: 2px;
    bottom: 2px;
    width: 2px;
    border-radius: 2px;
  }
  .border-red::before {
    background-color: #e57373; /* red lighten-2 */
  }
  .border-green::before {
    background-color: #81c784; /* green lighten-2 */
  }
  .border-yellow::before {
    background-color: #ffa000; /* amber darken-2 */
  }
</style>
